import { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../../components/ui/sheet';
import { Loader2, ShoppingCart, Wallet, Landmark, FileText, ArrowDown, HandCoins } from 'lucide-react';
import api from '../../../lib/api';
import { fmtFecha } from '../../../lib/utils';
import { fmtCLP, CONCILIACION_BADGE, TIPO_LABEL, PRIORIDAD_LABEL, EstadoBadge, MODOS } from './egresosUtils';

function Paso({ icon: Icon, titulo, ok, children, testId }) {
  return (
    <div className="relative pl-10" data-testid={testId}>
      <div className={`absolute left-0 top-0 flex h-8 w-8 items-center justify-center rounded-full ${ok ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>
        <Icon className="h-4 w-4" />
      </div>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{titulo}</p>
      <div className="mt-1 rounded-md border bg-white p-3 text-sm">{children}</div>
    </div>
  );
}

const Flecha = () => <div className="flex justify-center py-1 text-slate-300"><ArrowDown className="h-4 w-4" /></div>;

function PasoOC({ data, e }) {
  return (
    <Paso icon={ShoppingCart} titulo="Orden de compra" ok={!!data?.oc} testId="traza-oc">
      {data?.oc ? (
        <>
          <div className="flex items-center justify-between"><b>OC {data.oc.numero_oc}</b><span className="font-mono text-xs">{fmtCLP(data.oc.total)}</span></div>
          <div className="text-xs text-muted-foreground">{data.oc.nombre_proveedor} · {data.oc.fecha} · {data.oc.estado}{data.oc.ot_numero ? ` · OT ${data.oc.ot_numero}` : ''}</div>
        </>
      ) : <span className="text-xs text-muted-foreground">{e.oc_numero ? `OC ${e.oc_numero} (no encontrada en el ERP)` : 'Sin OC asociada — asóciala desde la tabla para la aplicación automática'}</span>}
    </Paso>
  );
}

function PasoDoc({ e, M }) {
  return (
    <Paso icon={M.key === 'ingreso' ? HandCoins : Wallet} titulo={M.label} ok testId={`traza-${M.key}`}>
      <div className="flex items-center justify-between"><b className="font-mono">{e.codigo_conciliacion}</b><span className="font-mono text-xs">{fmtCLP(e.monto)}</span></div>
      <div className="text-xs text-muted-foreground">{e.razon_social} · {e[M.rutCampo]} · {M.key === 'ingreso' ? 'cobrado' : 'pagado'} {fmtFecha(e.fecha_pago)} · {TIPO_LABEL[e.tipo] || e.tipo}{e.alegra_payment_id ? ` · Alegra #${e.alegra_payment_id}` : ''}</div>
      <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">
        <span>Aplicación:</span><EstadoBadge map={M.aplicacionBadge} estado={e.estado_aplicacion} />
        <span className="ml-2">Banco:</span><EstadoBadge map={CONCILIACION_BADGE} estado={e.estado_conciliacion_banco} />
      </div>
      <div className="mt-1 text-[11px] text-muted-foreground">Nombre conciliación: <span className="font-mono">{e.nombre_conciliacion}</span>{e.fecha_cartola_esperada ? ` · esperado en cartola ${fmtFecha(e.fecha_cartola_esperada)}` : ''}</div>
      {e.descripcion && <div className="mt-1 text-[11px] text-muted-foreground line-clamp-2" title={e.descripcion}>{e.descripcion}</div>}
    </Paso>
  );
}

function PasoMov({ e, mov, M }) {
  return (
    <Paso icon={Landmark} titulo="Movimiento bancario" ok={e.estado_conciliacion_banco === 'conciliado'} testId="traza-mov">
      {mov ? (
        <>
          <div className="flex items-center justify-between"><b>{fmtFecha(mov.fecha)}</b><span className={`font-mono text-xs ${M.colorMonto}`}>{fmtCLP(mov[M.montoMov])}</span></div>
          <div className="text-xs text-muted-foreground truncate" title={mov.detalle}>{mov.detalle}{mov.docto ? ` · doc ${mov.docto}` : ''} · cta {mov.cuenta}</div>
          <div className="text-[11px] mt-1">
            {e.estado_conciliacion_banco === 'conciliado'
              ? <span className="text-emerald-700">Conciliado {fmtFecha(e.fecha_conciliacion_banco)} · {PRIORIDAD_LABEL[e.conciliacion_prioridad] || e.conciliacion_prioridad}{e.conciliado_banco_por ? ` · ${e.conciliado_banco_por}` : ''}</span>
              : <span className="text-orange-700">Candidato sugerido (P3) — pendiente de revisión manual</span>}
          </div>
        </>
      ) : <span className="text-xs text-muted-foreground">{e.estado_conciliacion_banco === 'no_aplica' ? `Este ${M.label.toLowerCase()} no pasa por una cuenta con cartola (tarjeta / caja / fondo por rendir).` : 'Aún no aparece en la cartola bancaria.'}</span>}
    </Paso>
  );
}

function PasoFacturas({ e, facturas, M }) {
  const esIngreso = M.key === 'ingreso';
  const vacio = e.estado_aplicacion === 'no_aplica'
    ? (esIngreso ? 'Otro ingreso: no corresponde a facturas de venta.' : 'Gasto directo: no se aplica a facturas.')
    : (esIngreso ? 'Anticipo de cliente: aún sin factura de venta asociada.' : 'Sin factura aplicada todavía. El anticipo se aplicará al contabilizar la factura de la OC.');
  return (
    <Paso icon={FileText} titulo={`Factura${facturas.length === 1 ? '' : 's'}${esIngreso ? ' de venta' : ''}`} ok={facturas.length > 0} testId="traza-facturas">
      {facturas.length === 0 && <span className="text-xs text-muted-foreground">{vacio}</span>}
      {facturas.map((f) => (
        <div key={f.bill_id || f.invoice_id} className="border-b last:border-0 py-1">
          <div className="flex items-center justify-between">
            <b>Factura {f.numero || f.dte_folio || `#${f.bill_id || f.invoice_id}`}</b>
            <span className="font-mono text-xs">{fmtCLP(esIngreso ? f.monto : f.monto_aplicado)} {esIngreso ? 'cobrado' : 'aplicado'}</span>
          </div>
          <div className="text-xs text-muted-foreground">
            {f.proveedor_nombre || f.cliente_nombre || ''}{f.fecha ? ` · ${fmtFecha(f.fecha)}` : ''}{f.estado ? ` · ${f.estado}` : ''}
            {f.saldo !== undefined && f.saldo !== null ? ` · saldo ${fmtCLP(f.saldo)}` : ''}{f.automatica ? ' · aplicación automática' : ''}
            {f.fc_cxc ? ` · CxC flujo: ${f.fc_cxc.estado}` : ''}
          </div>
        </div>
      ))}
    </Paso>
  );
}

export function EgresoTrazabilidadSheet({ egreso, modo = 'egreso', onOpenChange }) {
  const M = MODOS[modo];
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!egreso) { setData(null); return; }
    setLoading(true);
    api.get(`${M.base}/${egreso.id}/trazabilidad`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [egreso, M.base]);

  const e = data?.egreso || data?.ingreso || egreso;
  const mov = data?.movimiento_bancario;
  const facturas = data?.facturas || [];
  const esIngreso = modo === 'ingreso';
  return (
    <Sheet open={!!egreso} onOpenChange={onOpenChange}>
      <SheetContent className="w-[520px] sm:max-w-[560px] overflow-y-auto" data-testid="sheet-trazabilidad">
        <SheetHeader>
          <SheetTitle>Trazabilidad del {M.label.toLowerCase()}</SheetTitle>
          <SheetDescription>{esIngreso ? 'Factura de venta → Cobro → Movimiento bancario' : 'OC → Egreso → Movimiento bancario → Factura'}. Cada eslabón mantiene su propio estado.</SheetDescription>
        </SheetHeader>
        {loading && <div className="py-6 text-center text-sm text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin mr-1" /> Cargando…</div>}
        {e && !loading && (
          <div className="mt-4 space-y-1">
            {esIngreso ? (
              <>
                <PasoFacturas e={e} facturas={facturas} M={M} />
                <Flecha />
                <PasoDoc e={e} M={M} />
                <Flecha />
                <PasoMov e={e} mov={mov} M={M} />
              </>
            ) : (
              <>
                <PasoOC data={data} e={e} />
                <Flecha />
                <PasoDoc e={e} M={M} />
                <Flecha />
                <PasoMov e={e} mov={mov} M={M} />
                <Flecha />
                <PasoFacturas e={e} facturas={facturas} M={M} />
              </>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
